import React, { useContext } from 'react';
import Skeleton from 'react-loading-skeleton';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { Empty, EmptyTable, HeaderCell, HeaderRow, IUserProps, SortLabel, TableBody, TableHead, Text, TooltipIcon, TooltipWrapper, User, UserTableDataProps } from 'components';
import { TableHeaderObject } from 'components/Table/User/UserTable';
import { theme } from 'design';
import { useWindowSize } from 'hooks';
import ListContext from 'providers/ListContextProvider';
import { ICurrentEmployee } from 'store/types';
import { EmployeeType, ISiteRolePolicy } from 'types';
import { formatJobTitle, isEmployeePayable } from 'utils';
import { orgRoleDisplay } from 'utils/employee';
import { CellAction, IAddButtonProps, JobTitleCell, JobTitleHeaderCell, PayeeCell, PayeesPaginationFooter, StyledRow, StyledTable, TableButton } from './SelectPayeesTableStyles';

interface ITableProps {
  tableHeaders: TableHeaderObject[];
  data?: UserTableDataProps[];
  isMultiSite?: boolean;
  isLoading?: boolean;
  onAddToPayeesList?: (id: string) => void;
  rolePolicy: ISiteRolePolicy;
  currentEmployee: ICurrentEmployee;
}

interface ISelectPayeeRowProps extends IUserProps, IAddButtonProps {
  jobTitle?: string;
  isLoading?: boolean;
  onClick?: () => void;
  hasPaymentMethod?: boolean;
  disabled?: boolean;
}

const DisabledWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  p {
    margin-right: 0;
  }
  svg {
    fill: ${theme.color.neutral[400]};
  }
`;

const SelectPayeeRow = ({ id, username, image, hasPaymentMethod, jobTitle, isAdded, isLoading, onClick, disabled = false, ...rest }: ISelectPayeeRowProps) => {
  // Query for displaying job title in User component
  // Aligns with css for hiding columns in table
  const windowSize = useWindowSize();
  const windowWidth = windowSize.width;

  const displayUserJobTitle = () => {
    if (windowWidth < theme.breakpoint.med) {
      return jobTitle;
    }
    if (windowWidth >= theme.breakpoint.large && windowWidth < theme.breakpoint.xlarge) {
      return jobTitle;
    }
    if (windowWidth < theme.breakpoint.xlarge) {
      return jobTitle;
    }
    return undefined;
  };

  return (
    <StyledRow {...rest} isActive={disabled}>
      <PayeeCell data-label="Name" align="left" data-cy="name">
        <User stackable={false} id={id} username={username} role={displayUserJobTitle()} image={image} hasPaymentMethod={hasPaymentMethod} isLoading={isLoading} wrapText disabled={disabled} />
      </PayeeCell>

      <JobTitleCell data-label="Job Title" align="left">
        {isLoading ? (
          <Skeleton width={77} />
        ) : (
          <Text size="small" className={disabled ? 'disabled' : ''}>
            {jobTitle}
          </Text>
        )}
      </JobTitleCell>

      <CellAction data-label="Add Employee" align="right">
        {disabled ? (
          <DisabledWrapper>
            <Text size="xxsmall" className="disabled">
              Ineligible for tips
            </Text>
            <TooltipWrapper>
              <TooltipIcon id={`employee_tt_${id}`} place="left">
                This user is ineligible to receive tips
              </TooltipIcon>
            </TooltipWrapper>
          </DisabledWrapper>
        ) : (
          <TableButton data-cy="payee-add-btn" data-value={id} label={isAdded ? 'Added' : 'Add'} appearance="primary" size="small" isAdded={isAdded} onClick={onClick} isLoading={isLoading} disabled={disabled} icon="CheckmarkThick" />
        )}
      </CellAction>
    </StyledRow>
  );
};

const SelectPayeesTable: React.FC<ITableProps> = ({ data, isMultiSite, isLoading, tableHeaders, onAddToPayeesList, rolePolicy, currentEmployee }) => {
  const paymentListContext = useContext(ListContext);
  const { pagination: Pager, resultsPerPage: ResultsPerPage } = paymentListContext.components;
  const { onSortChange, isSortedBy, isSortDesc } = paymentListContext.methods;
  const { limit } = paymentListContext.query;

  return (
    <>
      <StyledTable data-cy="select-payees-table">
        <TableHead>
          <HeaderRow bgColor="default">
            {tableHeaders.map(({ label, value, align }: TableHeaderObject, index: number) => {
              if (!isMultiSite && label === 'Site') {
                return null;
              }

              if (label === 'Job Title') {
                return (
                  <JobTitleHeaderCell key={index} align={align}>
                    {isLoading ? '' : label}
                  </JobTitleHeaderCell>
                );
              }

              if (label === '') {
                return (
                  <HeaderCell key={index} align={align}>
                    {label}
                  </HeaderCell>
                );
              }

              return (
                <HeaderCell key={index} align={align}>
                  <SortLabel label={label} isSorted={isSortedBy(value)} isSortedDesc={isSortDesc(value)} onClick={() => onSortChange(value)} isLoading={isLoading} />
                </HeaderCell>
              );
            })}
          </HeaderRow>
        </TableHead>

        <TableBody>
          {isLoading
            ? [...Array(limit)].map((e, i) => <SelectPayeeRow key={i} id={i.toString()} username="" isLoading />)
            : data &&
              data.map((row: UserTableDataProps) => {
                const { isAdded, username, avatarUrl, sites, type, hasPaymentMethod } = row;
                const isOrgAdmin = type === EmployeeType.ADMIN;
                const employeeSiteInfo = sites.reduce(
                  (acc, site) => {
                    if (!isOrgAdmin) {
                      acc.roles.push(orgRoleDisplay(type, site.role));
                      if (site.job) acc.jobs.push(formatJobTitle(site.job.title));
                      acc.sites.push(site.name);
                    }
                    return acc;
                  },
                  { roles: [], sites: [], jobs: [] }
                );

                return (
                  <SelectPayeeRow
                    key={`payee_${row.id}`}
                    id={`payee_${row.id}`}
                    disabled={!isEmployeePayable(row, rolePolicy, currentEmployee)}
                    username={username}
                    jobTitle={employeeSiteInfo.jobs.length === 1 ? employeeSiteInfo.jobs : ['Org Admin']}
                    image={avatarUrl}
                    hasPaymentMethod={hasPaymentMethod}
                    isAdded={isAdded}
                    isLoading={isLoading}
                    data-cy="select-payees-row"
                    data-value={row.id}
                    onClick={(e?: React.MouseEvent<HTMLElement>) => {
                      e?.stopPropagation();
                      onAddToPayeesList?.(row.id);
                    }}
                  />
                );
              })}
          {!isLoading && isEmpty(data) && (
            <EmptyTable>
              <Empty icon="People" message="No users found." />
            </EmptyTable>
          )}
        </TableBody>
      </StyledTable>
      <PayeesPaginationFooter>
        {!isLoading && (
          <>
            {ResultsPerPage}
            {Pager}
          </>
        )}
      </PayeesPaginationFooter>
    </>
  );
};

export default SelectPayeesTable;
